import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Calendar, BookOpen, TrendingUp, TrendingDown, DollarSign, MinusCircle, ListTree, ChevronDown, ChevronRight, Wallet, ArrowDownUp, Package, Target, ShieldCheck } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';

// ─── Types ──────────────────────────────────────────────────────────────────

interface PnLSummary {
  companyName: string;
  realmId: string;
  totalIncome: number;
  totalCOGS: number;
  grossProfit: number;
  totalExpenses: number;
  netOperatingIncome: number;
  otherIncome: number;
  otherExpenses: number;
  netIncome: number;
}

interface ConsolidatedPnL {
  companies: PnLSummary[];
  combined: PnLSummary;
}

interface MonthlyPnL {
  months: Array<{
    month: string;
    companies: PnLSummary[];
    combined: PnLSummary;
  }>;
}

interface ExpenseSubItem {
  name: string;
  amount: number;
  subItems?: ExpenseSubItem[];
}

interface ExpenseLineItem {
  category: string;
  amount: number;
  subItems: ExpenseSubItem[];
}

interface ExpenseBreakdown {
  companies: Array<{
    companyName: string;
    realmId: string;
    totalExpenses: number;
    categories: ExpenseLineItem[];
  }>;
}

interface BalanceSheetSummary {
  companyName: string;
  realmId: string;
  totalAssets: number;
  currentAssets: number;
  fixedAssets: number;
  otherAssets: number;
  totalLiabilities: number;
  currentLiabilities: number;
  longTermLiabilities: number;
  totalEquity: number;
}

interface CashFlowSummary {
  companyName: string;
  realmId: string;
  operatingActivities: number;
  investingActivities: number;
  financingActivities: number;
  netCashChange: number;
  endingCash: number;
}

interface COGSBreakdown {
  companies: Array<{
    companyName: string;
    realmId: string;
    totalCOGS: number;
    categories: ExpenseLineItem[];
  }>;
}

interface MonthlyNutCompany {
  companyName: string;
  realmId: string;
  monthlyNut: number;
  avgMonthlyVariable: number;
  avgMonthlyCOGS: number;
  avgMonthlyTotal: number;
  avgMonthlyRevenue: number;
  avgGrossProfit: number;
  grossMarginPct: number;
  breakEvenRevenue: number;
  marginBasedBreakEven: number;
  recurringItems: Array<{ name: string; avgMonthly: number }>;
  variableItems: Array<{ name: string; avgMonthly: number }>;
  monthlyDetail: Array<{
    month: string;
    revenue: number;
    grossProfit: number;
    grossMarginPct: number;
    recurring: number;
    variable: number;
    cogs: number;
    total: number;
    surplus: number;
    marginSurplus: number;
  }>;
}

interface MonthlyNutData {
  companies: MonthlyNutCompany[];
  combined: {
    monthlyNut: number;
    avgMonthlyVariable: number;
    avgMonthlyCOGS: number;
    avgMonthlyTotal: number;
    avgMonthlyRevenue: number;
    avgGrossProfit: number;
    grossMarginPct: number;
    breakEvenRevenue: number;
    marginBasedBreakEven: number;
  };
}

interface DataFreshnessMonth {
  month: string;
  status: 'closed' | 'reconciled' | 'active' | 'future';
  lastModified: string | null;
  transactionCount: number;
  recentEdits: number;
}

interface CompanyFreshness {
  realmId: string;
  companyName: string;
  bookClosedDate: string | null;
  months: DataFreshnessMonth[];
}

interface BSLineItem {
  name: string;
  amount: number;
  children: BSLineItem[];
}

interface DateRange {
  startDate: string;
  endDate: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const CHART_COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626'];

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const fmtK = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return fmt(n);
};

const shortName = (name: string) => {
  if (name.includes('ProShop')) return 'ProShop';
  if (name.includes('Custom Clubs')) return 'Custom Clubs';
  if (name.includes('Instruction')) return 'Instruction';
  return name;
};

const monthLabel = (m: string) => {
  const [, month] = m.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return names[parseInt(month, 10) - 1] || m;
};

// ─── Component ──────────────────────────────────────────────────────────────

// ─── Recursive Expense Row ──────────────────────────────────────────────

const ExpenseCategoryRow: React.FC<{
  name: string;
  amount: number;
  subItems?: ExpenseSubItem[];
  totalExpenses: number;
  expandedCategories: Set<string>;
  setExpandedCategories: React.Dispatch<React.SetStateAction<Set<string>>>;
  prefix: string;
  depth: number;
}> = ({ name, amount, subItems, totalExpenses, expandedCategories, setExpandedCategories, prefix, depth }) => {
  const rowKey = `${prefix}-${name}`;
  const isExpanded = expandedCategories.has(rowKey);
  const hasChildren = subItems && subItems.length > 0;
  const pctOfTotal = totalExpenses > 0 ? ((amount / totalExpenses) * 100).toFixed(1) : '0';
  const cleanName = name.replace(/^\d+\s*/, '');
  const indent = depth * 16;

  const bgClass = depth === 0 ? '' : depth === 1 ? 'bg-gray-50' : 'bg-gray-100';
  const textSize = depth === 0 ? 'text-sm' : 'text-xs';
  const fontWeight = depth === 0 ? 'font-medium' : '';

  return (
    <div>
      <button
        onClick={() => {
          if (!hasChildren) return;
          setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
          });
        }}
        className={`w-full px-5 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors text-left ${bgClass}`}
        style={{ paddingLeft: `${20 + indent}px` }}
      >
        <div className="flex items-center space-x-2 min-w-0">
          {hasChildren ? (
            isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          ) : (
            <div className="w-3.5 flex-shrink-0" />
          )}
          <span className={`${textSize} text-gray-700 truncate ${fontWeight}`}>{cleanName}</span>
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0">
          {depth === 0 && <span className="text-xs text-gray-400">{pctOfTotal}%</span>}
          <span className={`${textSize} ${fontWeight} text-gray-900`}>{fmt(amount)}</span>
        </div>
      </button>
      {isExpanded && hasChildren && (
        <div>
          {subItems!.map((sub) => (
            <ExpenseCategoryRow
              key={sub.name}
              name={sub.name}
              amount={sub.amount}
              subItems={sub.subItems}
              totalExpenses={totalExpenses}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              prefix={prefix}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Balance Sheet Drill-Down Row ───────────────────────────────────────────

const BSRow: React.FC<{ item: BSLineItem; depth: number }> = ({ item, depth }) => {
  const [expanded, setExpanded] = useState(depth < 1); // auto-expand first level
  const hasChildren = item.children.length > 0;
  const isTotal = item.name.toLowerCase().startsWith('total ');

  return (
    <>
      <tr
        className={`${hasChildren ? 'cursor-pointer hover:bg-gray-50' : ''} ${isTotal ? 'bg-gray-50 font-semibold' : ''}`}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <td className={`py-2 text-sm ${isTotal ? 'text-gray-900' : 'text-gray-700'}`} style={{ paddingLeft: `${1.5 + depth * 1.25}rem` }}>
          <div className="flex items-center gap-1">
            {hasChildren ? (
              expanded ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            ) : (
              <span className="w-3.5 flex-shrink-0" />
            )}
            <span>{item.name}</span>
          </div>
        </td>
        <td className={`px-6 py-2 text-sm text-right ${isTotal ? 'font-semibold' : ''} ${item.amount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {fmt(item.amount)}
        </td>
      </tr>
      {expanded && hasChildren && item.children.map((child, i) => (
        <BSRow key={`${child.name}-${i}`} item={child} depth={depth + 1} />
      ))}
    </>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

const QuickBooksFinancials: React.FC = () => {
  const [pnlData, setPnlData] = useState<ConsolidatedPnL | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyPnL | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [expenseData, setExpenseData] = useState<ExpenseBreakdown | null>(null);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [balanceSheetData, setBalanceSheetData] = useState<{ companies: BalanceSheetSummary[]; combined: BalanceSheetSummary } | null>(null);
  const [cashFlowData, setCashFlowData] = useState<{ companies: CashFlowSummary[]; combined: CashFlowSummary } | null>(null);
  const [cogsData, setCogsData] = useState<COGSBreakdown | null>(null);
  const [bsLoading, setBsLoading] = useState(false);
  const [cfLoading, setCfLoading] = useState(false);
  const [cogsLoading, setCogsLoading] = useState(false);
  const [nutData, setNutData] = useState<MonthlyNutData | null>(null);
  const [nutLoading, setNutLoading] = useState(false);
  const [nutMonths, setNutMonths] = useState(3);
  const [freshnessData, setFreshnessData] = useState<CompanyFreshness[] | null>(null);
  const [freshnessLoading, setFreshnessLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pnl' | 'charts' | 'trends' | 'expenses' | 'cogs' | 'balancesheet' | 'cashflow' | 'nut' | 'freshness'>('pnl');

  const currentYear = new Date().getFullYear();
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: `${currentYear}-01-01`,
    endDate: new Date().toISOString().split('T')[0],
  });
  const [trendYear, setTrendYear] = useState(currentYear);
  const [customDateRange, setCustomDateRange] = useState<DateRange>({ ...dateRange });
  const [showCustom, setShowCustom] = useState(false);

  const loadPnL = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/quickbooks/reports/consolidated-pnl?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
      );
      const result = await response.json();
      if (result.success) {
        setPnlData(result.data);
      }
    } catch (error) {
      console.error('Error loading consolidated P&L:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const loadMonthly = useCallback(async () => {
    try {
      setMonthlyLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/reports/monthly-pnl?year=${trendYear}`);
      const result = await response.json();
      if (result.success) {
        setMonthlyData(result.data);
      }
    } catch (error) {
      console.error('Error loading monthly P&L:', error);
    } finally {
      setMonthlyLoading(false);
    }
  }, [trendYear]);

  const loadExpenses = useCallback(async () => {
    try {
      setExpenseLoading(true);
      const response = await fetch(
        `${API_BASE}/quickbooks/reports/expense-breakdown?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
      );
      const result = await response.json();
      if (result.success) {
        setExpenseData(result.data);
      }
    } catch (error) {
      console.error('Error loading expense breakdown:', error);
    } finally {
      setExpenseLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const loadBalanceSheet = useCallback(async () => {
    try {
      setBsLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/reports/consolidated-balance-sheet?endDate=${dateRange.endDate}`);
      const result = await response.json();
      if (result.success) setBalanceSheetData(result.data);
    } catch (error) { console.error('Error loading balance sheet:', error); }
    finally { setBsLoading(false); }
  }, [dateRange.endDate]);

  const loadCashFlow = useCallback(async () => {
    try {
      setCfLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/reports/consolidated-cash-flow?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      const result = await response.json();
      if (result.success) setCashFlowData(result.data);
    } catch (error) { console.error('Error loading cash flow:', error); }
    finally { setCfLoading(false); }
  }, [dateRange.startDate, dateRange.endDate]);

  const loadCOGS = useCallback(async () => {
    try {
      setCogsLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/reports/cogs-breakdown?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      const result = await response.json();
      if (result.success) setCogsData(result.data);
    } catch (error) { console.error('Error loading COGS:', error); }
    finally { setCogsLoading(false); }
  }, [dateRange.startDate, dateRange.endDate]);

  const loadNut = useCallback(async () => {
    try {
      setNutLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/reports/monthly-nut?months=${nutMonths}`);
      const result = await response.json();
      if (result.success) setNutData(result.data);
    } catch (error) { console.error('Error loading monthly nut:', error); }
    finally { setNutLoading(false); }
  }, [nutMonths]);

  const loadFreshness = useCallback(async () => {
    try {
      setFreshnessLoading(true);
      const response = await fetch(`${API_BASE}/quickbooks/data-freshness`);
      const result = await response.json();
      if (result.success) setFreshnessData(result.data.companies);
    } catch (error) { console.error('Error loading data freshness:', error); }
    finally { setFreshnessLoading(false); }
  }, []);

  useEffect(() => { loadPnL(); }, [loadPnL]);
  useEffect(() => { if (activeTab === 'trends') loadMonthly(); }, [activeTab, loadMonthly]);
  useEffect(() => { if (activeTab === 'expenses') loadExpenses(); }, [activeTab, loadExpenses]);
  useEffect(() => { if (activeTab === 'balancesheet') loadBalanceSheet(); }, [activeTab, loadBalanceSheet]);
  useEffect(() => { if (activeTab === 'cashflow') loadCashFlow(); }, [activeTab, loadCashFlow]);
  useEffect(() => { if (activeTab === 'cogs') loadCOGS(); }, [activeTab, loadCOGS]);
  useEffect(() => { if (activeTab === 'nut') loadNut(); }, [activeTab, loadNut]);
  useEffect(() => { if (activeTab === 'freshness') loadFreshness(); }, [activeTab, loadFreshness]);

  // Date range presets
  const setPreset = (key: string) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let start: string, end: string;

    switch (key) {
      case 'thisMonth':
        start = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        end = now.toISOString().split('T')[0];
        break;
      case 'lastMonth': {
        const lm = m === 0 ? 11 : m - 1;
        const ly = m === 0 ? y - 1 : y;
        const lastDay = new Date(ly, lm + 1, 0).getDate();
        start = `${ly}-${String(lm + 1).padStart(2, '0')}-01`;
        end = `${ly}-${String(lm + 1).padStart(2, '0')}-${lastDay}`;
        break;
      }
      case 'thisQuarter': {
        const qStart = Math.floor(m / 3) * 3;
        start = `${y}-${String(qStart + 1).padStart(2, '0')}-01`;
        end = now.toISOString().split('T')[0];
        break;
      }
      case 'thisYear':
        start = `${y}-01-01`;
        end = now.toISOString().split('T')[0];
        break;
      case 'lastYear':
        start = `${y - 1}-01-01`;
        end = `${y - 1}-12-31`;
        break;
      default:
        return;
    }
    setDateRange({ startDate: start, endDate: end });
    setCustomDateRange({ startDate: start, endDate: end });
    setShowCustom(false);
  };

  const applyCustomRange = () => {
    setDateRange(customDateRange);
    setShowCustom(false);
  };

  const formatDateLabel = () => {
    const s = new Date(dateRange.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(dateRange.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} - ${e}`;
  };

  if (loading && !pnlData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading financial data...</span>
      </div>
    );
  }

  if (!pnlData) {
    return (
      <div className="text-center py-16 text-gray-500">
        Unable to load financial data. Make sure QuickBooks companies are connected.
      </div>
    );
  }

  const { companies, combined } = pnlData;

  // Prepare chart data
  const comparisonData = [
    { name: 'Revenue', ...Object.fromEntries(companies.map(c => [shortName(c.companyName), c.totalIncome])) },
    { name: 'COGS', ...Object.fromEntries(companies.map(c => [shortName(c.companyName), c.totalCOGS])) },
    { name: 'Expenses', ...Object.fromEntries(companies.map(c => [shortName(c.companyName), c.totalExpenses])) },
    { name: 'Net Income', ...Object.fromEntries(companies.map(c => [shortName(c.companyName), c.netIncome])) },
  ];

  const pieData = companies.map((c, i) => ({
    name: shortName(c.companyName),
    value: Math.max(0, c.totalIncome),
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  // Monthly trend line chart data
  const trendData = monthlyData?.months.map(m => {
    const row: Record<string, any> = { month: monthLabel(m.month) };
    m.companies.forEach(c => {
      row[shortName(c.companyName)] = c.totalIncome;
    });
    row['Combined'] = m.combined.totalIncome;
    return row;
  }) || [];

  const companyShortNames = companies.map(c => shortName(c.companyName));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QuickBooks Financials</h1>
          <p className="text-gray-600">{formatDateLabel()}</p>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          {[
            { key: 'thisMonth', label: 'This Month' },
            { key: 'lastMonth', label: 'Last Month' },
            { key: 'thisQuarter', label: 'This Quarter' },
            { key: 'thisYear', label: 'This Year' },
            { key: 'lastYear', label: 'Last Year' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                !showCustom && dateRange.startDate === customDateRange.startDate
                  ? 'bg-gray-100 text-gray-700'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setShowCustom(!showCustom)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              showCustom ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Custom
          </button>
          {showCustom && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md"
              />
              <button
                onClick={applyCustomRange}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-500">Total Revenue</p>
            <DollarSign className="h-5 w-5 text-green-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{fmtK(combined.totalIncome)}</p>
          <p className="mt-1 text-xs text-gray-500">{companies.length} companies</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-500">Total COGS</p>
            <MinusCircle className="h-5 w-5 text-orange-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{fmtK(combined.totalCOGS)}</p>
          <p className="mt-1 text-xs text-gray-500">
            {combined.totalIncome > 0 ? `${((combined.totalCOGS / combined.totalIncome) * 100).toFixed(1)}% of revenue` : ''}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-500">Total Expenses</p>
            <TrendingDown className="h-5 w-5 text-red-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900">{fmtK(combined.totalExpenses)}</p>
          <p className="mt-1 text-xs text-gray-500">
            {combined.totalIncome > 0 ? `${((combined.totalExpenses / combined.totalIncome) * 100).toFixed(1)}% of revenue` : ''}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-500">Net Income</p>
            {combined.netIncome >= 0 ? (
              <TrendingUp className="h-5 w-5 text-green-500" />
            ) : (
              <TrendingDown className="h-5 w-5 text-red-500" />
            )}
          </div>
          <p className={`mt-2 text-2xl font-bold ${combined.netIncome >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {fmtK(combined.netIncome)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {combined.totalIncome > 0 ? `${((combined.netIncome / combined.totalIncome) * 100).toFixed(1)}% margin` : ''}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { key: 'pnl', label: 'Profit & Loss', icon: BookOpen },
            { key: 'charts', label: 'Entity Comparison', icon: TrendingUp },
            { key: 'trends', label: 'Monthly Trends', icon: Calendar },
            { key: 'expenses', label: 'Expense Breakdown', icon: ListTree },
            { key: 'cogs', label: 'COGS', icon: Package },
            { key: 'balancesheet', label: 'Balance Sheet', icon: Wallet },
            { key: 'cashflow', label: 'Cash Flow', icon: ArrowDownUp },
            { key: 'nut', label: 'Monthly Nut', icon: Target },
            { key: 'freshness', label: 'Data Status', icon: ShieldCheck },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="h-4 w-4 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'pnl' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Line Item
                  </th>
                  {companies.map(c => (
                    <th key={c.realmId} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {shortName(c.companyName)}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
                    Combined
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[
                  { label: 'Total Income', key: 'totalIncome' as const, bold: false },
                  { label: 'Cost of Goods Sold', key: 'totalCOGS' as const, bold: false },
                  { label: 'Gross Profit', key: 'grossProfit' as const, bold: true },
                  { label: 'Total Expenses', key: 'totalExpenses' as const, bold: false },
                  { label: 'Net Operating Income', key: 'netOperatingIncome' as const, bold: true },
                  { label: 'Other Income', key: 'otherIncome' as const, bold: false },
                  { label: 'Other Expenses', key: 'otherExpenses' as const, bold: false },
                  { label: 'Net Income', key: 'netIncome' as const, bold: true },
                ].map(({ label, key, bold }) => (
                  <tr key={key} className={bold ? 'bg-gray-50' : ''}>
                    <td className={`px-6 py-3 text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {label}
                    </td>
                    {companies.map(c => {
                      const val = c[key];
                      return (
                        <td key={c.realmId} className={`px-6 py-3 text-sm text-right ${
                          bold ? 'font-semibold' : ''
                        } ${val < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {fmt(val)}
                        </td>
                      );
                    })}
                    <td className={`px-6 py-3 text-sm text-right bg-gray-50 ${
                      bold ? 'font-semibold' : ''
                    } ${combined[key] < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {fmt(combined[key])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'charts' && (
        <div className="space-y-6">
          {/* Entity Comparison Bar Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Entity Comparison</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={comparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => fmtK(v)} />
                <Tooltip formatter={(value: number) => fmt(value)} />
                <Legend />
                {companyShortNames.map((name, i) => (
                  <Bar key={name} dataKey={name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue Share Pie Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Share</h3>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => fmt(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Entity Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {companies.map((c, i) => (
              <div key={c.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i] }} />
                  <h4 className="font-medium text-gray-900">{shortName(c.companyName)}</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Revenue</span>
                    <span className="font-medium">{fmt(c.totalIncome)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gross Profit</span>
                    <span className={`font-medium ${c.grossProfit < 0 ? 'text-red-600' : ''}`}>{fmt(c.grossProfit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Net Income</span>
                    <span className={`font-semibold ${c.netIncome < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {fmt(c.netIncome)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Net Margin</span>
                    <span className={`font-medium ${c.netIncome < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {c.totalIncome > 0 ? `${((c.netIncome / c.totalIncome) * 100).toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="space-y-6">
          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Year:</span>
            {[currentYear - 1, currentYear].map(y => (
              <button
                key={y}
                onClick={() => setTrendYear(y)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  trendYear === y ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          {monthlyLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading monthly data (this may take a moment)...</span>
            </div>
          ) : trendData.length > 0 ? (
            <>
              {/* Revenue Trends Line Chart */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Revenue by Entity</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => fmtK(v)} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    {companyShortNames.map((name, i) => (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="Combined"
                      stroke="#374151"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Net Income Trends */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Net Income by Entity</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={monthlyData?.months.map(m => {
                      const row: Record<string, any> = { month: monthLabel(m.month) };
                      m.companies.forEach(c => { row[shortName(c.companyName)] = c.netIncome; });
                      return row;
                    }) || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => fmtK(v)} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    {companyShortNames.map((name, i) => (
                      <Bar key={name} dataKey={name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No monthly data available.</div>
          )}
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="space-y-6">
          {expenseLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading expense breakdown...</span>
            </div>
          ) : expenseData && expenseData.companies.length > 0 ? (
            <>
              {/* Horizontal bar chart comparing top expense categories across entities */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Top Expense Categories by Entity</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    layout="vertical"
                    data={(() => {
                      // Collect all unique categories, pick top 10 by combined total
                      const categoryTotals = new Map<string, number>();
                      expenseData.companies.forEach(c => {
                        c.categories.forEach(cat => {
                          const clean = cat.category.replace(/^\d+\s*/, '');
                          categoryTotals.set(clean, (categoryTotals.get(clean) || 0) + cat.amount);
                        });
                      });
                      const topCategories = [...categoryTotals.entries()]
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([name]) => name);

                      return topCategories.map(catName => {
                        const row: Record<string, any> = { name: catName };
                        expenseData.companies.forEach(c => {
                          const match = c.categories.find(cat => cat.category.replace(/^\d+\s*/, '') === catName);
                          row[shortName(c.companyName)] = match?.amount || 0;
                        });
                        return row;
                      });
                    })()}
                    margin={{ top: 5, right: 30, left: 200, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => fmtK(v)} />
                    <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    {expenseData.companies.map((c, i) => (
                      <Bar key={c.realmId} dataKey={shortName(c.companyName)} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Detailed expense tables per company */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {expenseData.companies.map((company, ci) => (
                  <div key={company.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[ci] }} />
                          <h4 className="font-medium text-gray-900">{shortName(company.companyName)}</h4>
                        </div>
                        <span className="text-sm font-semibold text-red-600">{fmt(company.totalExpenses)}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {company.categories.map((cat) => (
                        <ExpenseCategoryRow
                          key={cat.category}
                          name={cat.category}
                          amount={cat.amount}
                          subItems={cat.subItems}
                          totalExpenses={company.totalExpenses}
                          expandedCategories={expandedCategories}
                          setExpandedCategories={setExpandedCategories}
                          prefix={company.realmId}
                          depth={0}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No expense data available.</div>
          )}
        </div>
      )}

      {/* COGS Tab */}
      {activeTab === 'cogs' && (
        <div className="space-y-6">
          {cogsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading COGS breakdown...</span>
            </div>
          ) : cogsData && cogsData.companies.length > 0 ? (
            <>
              {/* COGS comparison bar chart */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">COGS by Category Across Entities</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    layout="vertical"
                    data={(() => {
                      const catTotals = new Map<string, number>();
                      cogsData.companies.forEach(c => c.categories.forEach(cat => {
                        const clean = cat.category.replace(/^\d+\s*/, '');
                        catTotals.set(clean, (catTotals.get(clean) || 0) + cat.amount);
                      }));
                      return [...catTotals.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10).map(([name]) => {
                        const row: Record<string, any> = { name };
                        cogsData.companies.forEach(c => {
                          const match = c.categories.find(cat => cat.category.replace(/^\d+\s*/, '') === name);
                          row[shortName(c.companyName)] = match?.amount || 0;
                        });
                        return row;
                      });
                    })()}
                    margin={{ top: 5, right: 30, left: 200, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => fmtK(v)} />
                    <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    {cogsData.companies.map((c, i) => (
                      <Bar key={c.realmId} dataKey={shortName(c.companyName)} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* COGS detail per company */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {cogsData.companies.map((company, ci) => (
                  <div key={company.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[ci] }} />
                          <h4 className="font-medium text-gray-900">{shortName(company.companyName)}</h4>
                        </div>
                        <span className="text-sm font-semibold text-orange-600">{fmt(company.totalCOGS)}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {company.categories.map((cat) => (
                        <ExpenseCategoryRow
                          key={cat.category}
                          name={cat.category}
                          amount={cat.amount}
                          subItems={cat.subItems}
                          totalExpenses={company.totalCOGS}
                          expandedCategories={expandedCategories}
                          setExpandedCategories={setExpandedCategories}
                          prefix={`cogs-${company.realmId}`}
                          depth={0}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No COGS data available.</div>
          )}
        </div>
      )}

      {/* Balance Sheet Tab */}
      {activeTab === 'balancesheet' && (
        <div className="space-y-6">
          {bsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading balance sheet...</span>
            </div>
          ) : balanceSheetData ? (
            <>
              {/* Combined Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-sm font-medium text-gray-500">Total Assets</p>
                  <p className="mt-1 text-2xl font-bold text-blue-700">{fmtK(balanceSheetData.combined.totalAssets)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-sm font-medium text-gray-500">Total Liabilities</p>
                  <p className="mt-1 text-2xl font-bold text-red-600">{fmtK(balanceSheetData.combined.totalLiabilities)}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-sm font-medium text-gray-500">Total Equity</p>
                  <p className={`mt-1 text-2xl font-bold ${balanceSheetData.combined.totalEquity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {fmtK(balanceSheetData.combined.totalEquity)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-sm font-medium text-gray-500">Debt-to-Equity</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {balanceSheetData.combined.totalEquity !== 0
                      ? `${(balanceSheetData.combined.totalLiabilities / Math.abs(balanceSheetData.combined.totalEquity)).toFixed(2)}x`
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Per-Company Drill-Down Cards */}
              {balanceSheetData.companies.map((company: any, idx: number) => (
                <div key={company.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                      <h3 className="text-lg font-medium text-gray-900">{shortName(company.companyName)}</h3>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-blue-600 font-medium">Assets: {fmt(company.totalAssets)}</span>
                      <span className="text-red-600 font-medium">Liabilities: {fmt(company.totalLiabilities)}</span>
                      <span className={`font-medium ${company.totalEquity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        Equity: {fmt(company.totalEquity)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                    {/* Assets */}
                    <div className="p-4">
                      <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 px-1.5">Assets</h4>
                      <table className="w-full">
                        <tbody>
                          {(company.assetDetail || []).map((item: BSLineItem, i: number) => (
                            <BSRow key={`${item.name}-${i}`} item={item} depth={0} />
                          ))}
                          <tr className="border-t border-gray-300 bg-blue-50 font-bold">
                            <td className="py-2 pl-1.5 text-sm text-blue-800">TOTAL ASSETS</td>
                            <td className="px-6 py-2 text-sm text-right text-blue-800">{fmt(company.totalAssets)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Liabilities */}
                    <div className="p-4">
                      <h4 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-2 px-1.5">Liabilities</h4>
                      <table className="w-full">
                        <tbody>
                          {(company.liabilityDetail || []).map((item: BSLineItem, i: number) => (
                            <BSRow key={`${item.name}-${i}`} item={item} depth={0} />
                          ))}
                          <tr className="border-t border-gray-300 bg-red-50 font-bold">
                            <td className="py-2 pl-1.5 text-sm text-red-800">TOTAL LIABILITIES</td>
                            <td className="px-6 py-2 text-sm text-right text-red-800">{fmt(company.totalLiabilities)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Equity */}
                    <div className="p-4">
                      <h4 className="text-xs font-bold text-green-700 uppercase tracking-wider mb-2 px-1.5">Equity</h4>
                      <table className="w-full">
                        <tbody>
                          {(company.equityDetail || []).map((item: BSLineItem, i: number) => (
                            <BSRow key={`${item.name}-${i}`} item={item} depth={0} />
                          ))}
                          <tr className="border-t border-gray-300 bg-green-50 font-bold">
                            <td className="py-2 pl-1.5 text-sm text-green-800">TOTAL EQUITY</td>
                            <td className={`px-6 py-2 text-sm text-right font-bold ${company.totalEquity < 0 ? 'text-red-800' : 'text-green-800'}`}>
                              {fmt(company.totalEquity)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}

              {/* Balance Sheet Bar Chart */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Assets vs Liabilities vs Equity</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={balanceSheetData.companies.map((c: any) => ({
                      name: shortName(c.companyName),
                      Assets: c.totalAssets,
                      Liabilities: c.totalLiabilities,
                      Equity: c.totalEquity,
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => fmtK(v)} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    <Bar dataKey="Assets" fill="#2563eb" />
                    <Bar dataKey="Liabilities" fill="#dc2626" />
                    <Bar dataKey="Equity" fill="#059669" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No balance sheet data available.</div>
          )}
        </div>
      )}

      {/* Cash Flow Tab */}
      {activeTab === 'cashflow' && (
        <div className="space-y-6">
          {cfLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading cash flow...</span>
            </div>
          ) : cashFlowData ? (
            <>
              {/* Cash Flow Table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Flow Item</th>
                        {cashFlowData.companies.map(c => (
                          <th key={c.realmId} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {shortName(c.companyName)}
                          </th>
                        ))}
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Combined</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {([
                        { label: 'Operating Activities', key: 'operatingActivities' as const },
                        { label: 'Investing Activities', key: 'investingActivities' as const },
                        { label: 'Financing Activities', key: 'financingActivities' as const },
                      ]).map(({ label, key }) => (
                        <tr key={key}>
                          <td className="px-6 py-3 text-sm text-gray-700">{label}</td>
                          {cashFlowData.companies.map(c => (
                            <td key={c.realmId} className={`px-6 py-3 text-sm text-right ${c[key] < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                              {fmt(c[key])}
                            </td>
                          ))}
                          <td className={`px-6 py-3 text-sm text-right bg-gray-50 ${cashFlowData.combined[key] < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {fmt(cashFlowData.combined[key])}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-6 py-3 text-sm text-gray-900">Net Cash Change</td>
                        {cashFlowData.companies.map(c => (
                          <td key={c.realmId} className={`px-6 py-3 text-sm text-right ${c.netCashChange < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {fmt(c.netCashChange)}
                          </td>
                        ))}
                        <td className={`px-6 py-3 text-sm text-right bg-gray-100 ${cashFlowData.combined.netCashChange < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {fmt(cashFlowData.combined.netCashChange)}
                        </td>
                      </tr>
                      <tr className="font-semibold">
                        <td className="px-6 py-3 text-sm text-gray-900">Ending Cash Balance</td>
                        {cashFlowData.companies.map(c => (
                          <td key={c.realmId} className="px-6 py-3 text-sm text-right text-gray-900">{fmt(c.endingCash)}</td>
                        ))}
                        <td className="px-6 py-3 text-sm text-right bg-gray-50 text-gray-900">{fmt(cashFlowData.combined.endingCash)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cash Flow Bar Chart */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Cash Flow by Activity Type</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={cashFlowData.companies.map(c => ({
                      name: shortName(c.companyName),
                      Operating: c.operatingActivities,
                      Investing: c.investingActivities,
                      Financing: c.financingActivities,
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => fmtK(v)} />
                    <Tooltip formatter={(value: number) => fmt(value)} />
                    <Legend />
                    <Bar dataKey="Operating" fill="#2563eb" />
                    <Bar dataKey="Investing" fill="#d97706" />
                    <Bar dataKey="Financing" fill="#7c3aed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No cash flow data available.</div>
          )}
        </div>
      )}

      {/* Monthly Nut / Break-Even Tab */}
      {activeTab === 'nut' && (
        <div className="space-y-6">
          {/* Lookback selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Averaging last:</span>
            {[3, 6, 12].map(m => (
              <button
                key={m}
                onClick={() => setNutMonths(m)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  nutMonths === m ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {m} months
              </button>
            ))}
          </div>

          {/* Data quality note */}
          {nutData && nutData.companies?.some((c: any) => c.dataNote) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Using reconciled data only</span>
              </div>
              {nutData.companies?.map((c: any) => c.dataNote && (
                <p key={c.realmId} className="text-xs text-blue-700 ml-6">
                  <strong>{shortName(c.companyName)}:</strong> {c.dataNote}
                  {c.monthsUsed?.length > 0 && (
                    <span className="text-blue-500"> ({c.monthsUsed.join(', ')})</span>
                  )}
                </p>
              ))}
            </div>
          )}

          {nutLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Calculating monthly nut (checking reconciled months)...</span>
            </div>
          ) : nutData ? (
            <>
              {/* Combined KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase">Monthly Nut (Fixed)</p>
                  <p className="mt-1 text-xl font-bold text-red-600">{fmtK(nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable)}</p>
                  <p className="text-xs text-gray-400">
                    {fmtK(nutData.combined.monthlyNut)} recurring + {fmtK(nutData.combined.avgMonthlyVariable)} variable
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase">Gross Margin</p>
                  <p className="mt-1 text-xl font-bold text-blue-600">{nutData.combined.grossMarginPct.toFixed(1)}%</p>
                  <p className="text-xs text-gray-400">
                    {fmtK(nutData.combined.avgGrossProfit)}/mo after COGS
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase">Break-Even Revenue</p>
                  <p className="mt-1 text-xl font-bold text-gray-900">{fmtK(nutData.combined.marginBasedBreakEven)}</p>
                  <p className="text-xs text-gray-400">
                    Nut ÷ {nutData.combined.grossMarginPct.toFixed(0)}% margin
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase">Revenue vs Target</p>
                  <p className={`mt-1 text-xl font-bold ${nutData.combined.avgMonthlyRevenue >= nutData.combined.marginBasedBreakEven ? 'text-green-600' : 'text-red-600'}`}>
                    {fmtK(nutData.combined.avgMonthlyRevenue)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {nutData.combined.avgMonthlyRevenue >= nutData.combined.marginBasedBreakEven ? '✅' : '⚠️'}{' '}
                    {((nutData.combined.avgMonthlyRevenue / (nutData.combined.marginBasedBreakEven || 1)) * 100).toFixed(0)}% of break-even
                  </p>
                </div>
              </div>

              {/* Margin Insight Banner */}
              <div className={`rounded-lg p-4 ${nutData.combined.avgGrossProfit >= (nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable) ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-sm font-medium ${nutData.combined.avgGrossProfit >= (nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable) ? 'text-green-800' : 'text-red-800'}`}>
                  {nutData.combined.avgGrossProfit >= (nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable)
                    ? `✅ Gross profit (${fmtK(nutData.combined.avgGrossProfit)}/mo) covers your fixed costs (${fmtK(nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable)}/mo) by ${fmtK(nutData.combined.avgGrossProfit - (nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable))}`
                    : `⚠️ Gross profit (${fmtK(nutData.combined.avgGrossProfit)}/mo) does NOT cover fixed costs (${fmtK(nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable)}/mo) — short by ${fmtK((nutData.combined.monthlyNut + nutData.combined.avgMonthlyVariable) - nutData.combined.avgGrossProfit)}/mo`
                  }
                </p>
              </div>

              {/* Monthly Gross Profit vs Fixed Costs Chart */}
              {nutData.companies.length > 0 && nutData.companies[0].monthlyDetail.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Gross Profit vs Fixed Costs (Combined)</h3>
                  <p className="text-xs text-gray-500 mb-4">Green bar (GP after COGS) must exceed red+orange (fixed costs) to break even</p>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={(() => {
                        const monthMap = new Map<string, { revenue: number; grossProfit: number; recurring: number; variable: number; cogs: number; margin: number }>();
                        nutData.companies.forEach(c => c.monthlyDetail.forEach(m => {
                          const existing = monthMap.get(m.month) || { revenue: 0, grossProfit: 0, recurring: 0, variable: 0, cogs: 0, margin: 0 };
                          existing.revenue += m.revenue;
                          existing.grossProfit += m.grossProfit;
                          existing.recurring += m.recurring;
                          existing.variable += m.variable;
                          existing.cogs += m.cogs;
                          monthMap.set(m.month, existing);
                        }));
                        return [...monthMap.entries()].sort().map(([month, d]) => ({
                          month: monthLabel(month),
                          'Gross Profit': d.grossProfit,
                          'Recurring': d.recurring,
                          'Variable': d.variable,
                          'Margin %': d.revenue > 0 ? ((d.grossProfit / d.revenue) * 100).toFixed(1) : '0',
                        }));
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(v) => fmtK(v)} />
                      <Tooltip formatter={(value: number, name: string) => name === 'Margin %' ? `${value}%` : fmt(value as number)} />
                      <Legend />
                      <Bar dataKey="Gross Profit" fill="#059669" />
                      <Bar dataKey="Recurring" fill="#dc2626" stackId="fixed" />
                      <Bar dataKey="Variable" fill="#d97706" stackId="fixed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Per-Company Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {nutData.companies.map((company, ci) => (
                  <div key={company.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-5 py-4 border-b border-gray-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[ci] }} />
                        <h4 className="font-medium text-gray-900">{shortName(company.companyName)}</h4>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Fixed Costs</span>
                          <p className="font-semibold text-red-600">{fmtK(company.monthlyNut + company.avgMonthlyVariable)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Gross Margin</span>
                          <p className="font-semibold text-blue-600">{company.grossMarginPct.toFixed(1)}%</p>
                        </div>
                        <div>
                          <span className="text-gray-500">BE Revenue</span>
                          <p className="font-semibold">{fmtK(company.marginBasedBreakEven)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Avg Revenue</span>
                          <p className="font-semibold text-gray-900">{fmtK(company.avgMonthlyRevenue)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Avg GP</span>
                          <p className="font-semibold text-green-600">{fmtK(company.avgGrossProfit)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">GP vs Nut</span>
                          <p className={`font-semibold ${company.avgGrossProfit >= (company.monthlyNut + company.avgMonthlyVariable) ? 'text-green-600' : 'text-red-600'}`}>
                            {company.avgGrossProfit >= (company.monthlyNut + company.avgMonthlyVariable) ? '+' : ''}{fmtK(company.avgGrossProfit - (company.monthlyNut + company.avgMonthlyVariable))}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Recurring expenses list */}
                    <div className="px-5 py-3 border-b border-gray-100">
                      <p className="text-xs font-bold text-red-600 uppercase mb-2">Recurring ({fmt(company.monthlyNut)}/mo)</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {company.recurringItems.slice(0, 15).map(item => (
                          <div key={item.name} className="flex justify-between text-xs">
                            <span className="text-gray-600 truncate mr-2">{item.name.replace(/^\d+\s*/, '')}</span>
                            <span className="font-medium text-gray-900 flex-shrink-0">{fmt(item.avgMonthly)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Variable expenses list */}
                    <div className="px-5 py-3">
                      <p className="text-xs font-bold text-orange-600 uppercase mb-2">Variable ({fmt(company.avgMonthlyVariable)}/mo avg)</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {company.variableItems.slice(0, 15).map(item => (
                          <div key={item.name} className="flex justify-between text-xs">
                            <span className="text-gray-600 truncate mr-2">{item.name.replace(/^\d+\s*/, '')}</span>
                            <span className="font-medium text-gray-900 flex-shrink-0">{fmt(item.avgMonthly)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">No break-even data available.</div>
          )}
        </div>
      )}
      {activeTab === 'freshness' && (
        <div className="space-y-6">
          {freshnessLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Analyzing data freshness (checking transaction activity)...</span>
            </div>
          ) : freshnessData && freshnessData.length > 0 ? (
            <>
              {/* Legend */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Data Status Legend</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500" />
                    <span><strong>Closed</strong> — Books locked, data finalized</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span><strong>Reconciled</strong> — No recent edits, likely complete</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500" />
                    <span><strong>Active</strong> — Transactions still being entered/modified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-gray-300" />
                    <span><strong>Future</strong> — No data expected yet</span>
                  </div>
                </div>
              </div>

              {/* Per-Company Freshness */}
              {freshnessData.map(company => (
                <div key={company.realmId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{shortName(company.companyName)}</h3>
                    {company.bookClosedDate && (
                      <span className="text-sm bg-green-50 text-green-700 px-3 py-1 rounded-full">
                        Books closed through {new Date(company.bookClosedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                    {!company.bookClosedDate && (
                      <span className="text-sm bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full">
                        ⚠ "Close the Books" not enabled in QBO
                      </span>
                    )}
                  </div>

                  {/* Month Grid */}
                  <div className="grid grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-2">
                    {company.months.map(m => {
                      const statusColors = {
                        closed: 'bg-green-100 border-green-300 text-green-800',
                        reconciled: 'bg-blue-100 border-blue-300 text-blue-800',
                        active: 'bg-yellow-100 border-yellow-300 text-yellow-800',
                        future: 'bg-gray-50 border-gray-200 text-gray-400',
                      };
                      const statusDots = {
                        closed: 'bg-green-500',
                        reconciled: 'bg-blue-500',
                        active: 'bg-yellow-500',
                        future: 'bg-gray-300',
                      };
                      const [, month] = m.month.split('-');
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const label = monthNames[parseInt(month, 10) - 1];

                      return (
                        <div
                          key={m.month}
                          className={`p-2 rounded-lg border text-center ${statusColors[m.status]}`}
                          title={`${m.month}: ${m.transactionCount} txns, ${m.recentEdits} edited in last 7d${m.lastModified ? ', last edit: ' + new Date(m.lastModified).toLocaleDateString() : ''}`}
                        >
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <div className={`w-2 h-2 rounded-full ${statusDots[m.status]}`} />
                            <span className="text-xs font-medium">{label}</span>
                          </div>
                          <div className="text-[10px]">{m.transactionCount} txns</div>
                          {m.recentEdits > 0 && (
                            <div className="text-[10px] font-medium">
                              {m.recentEdits} edited
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Use date ranges that end on a "Closed" or "Reconciled" month for the most accurate financial analysis.
                  Months marked "Active" may have incomplete or unreconciled data. Enable "Close the Books" in QBO Settings → Advanced
                  to formally lock completed periods.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-16 text-gray-500">Unable to load data freshness status.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickBooksFinancials;
